/**
 * Storage Routes (Hono + OpenAPI)
 *
 * Custom endpoints layered on top of auto-mounted CRUD:
 * - POST /upload/multiple — multipart upload (Web parseBody)
 * - GET  /stats, /stats/by-type, /stats/by-folder — dashboard widgets
 * - GET  /files — overrides auto-mounted list to add ?tags= filter + ?include=tags enrichment
 * - DELETE /files/{id} — overrides auto-mount with per-instance CASL check + entity.deleted event
 *
 * CRUD for storage_files + storage_configs is auto-mounted by setupModuleRoutes (step 2c).
 * Custom routes register first, so they take precedence over auto-mount (Hono first-match wins).
 */

import { OpenAPIHono, createRoute, z } from '@hono/zod-openapi'
import type { MiddlewareHandler } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import { formatBytes } from '@gzl10/nexus-sdk'
import type { NexusEnv, OpenApiHandler } from '../../http/types.js'
import { createRateLimit } from '../../http/middleware/rate-limit.middleware.js'
import { createAttachmentsRoutes } from './attachments.routes.js'
import { getStorageService } from './storage.service.js'
import type { UploadedFile } from './storage.types.js'

export function createStorageRoutes(ctx: ModuleContext) {
  const app = new OpenAPIHono<NexusEnv>()

  const authMw = ctx.core.middleware['auth']
  const auth = authMw as unknown as MiddlewareHandler<NexusEnv> | undefined
  const optionalAuthMw = ctx.core.middleware['optionalAuth']
  const optionalAuth = optionalAuthMw as unknown as MiddlewareHandler<NexusEnv> | undefined

  const anyObject = z.object({}).passthrough()
  const idParams = z.object({ id: z.string() })

  const uploadRateLimit = createRateLimit({
    windowMs: 60 * 1000,
    max: 20,
    message: 'Too many uploads, try again in 1 minute'
  }) as unknown as MiddlewareHandler<NexusEnv>

  // ============================================================================
  // POST /upload/multiple
  // ============================================================================

  const uploadMultipleRoute = createRoute({
    method: 'post',
    path: '/upload/multiple',
    tags: ['Storage'],
    summary: 'Upload multiple files',
    security: auth ? [{ bearerAuth: [] }] : [],
    middleware: (auth ? [uploadRateLimit, auth] : [uploadRateLimit]) as unknown as MiddlewareHandler<NexusEnv>[],
    request: {
      body: {
        content: {
          'multipart/form-data': {
            schema: z.object({
              files: z.array(z.any()).optional(),
              folder: z.string().optional()
            })
          }
        }
      }
    },
    responses: {
      201: { description: 'Files uploaded', content: { 'application/json': { schema: z.array(anyObject) } } },
      400: { description: 'Validation error', content: { 'application/json': { schema: anyObject } } }
    }
  })

  app.openapi(uploadMultipleRoute, (async (c) => {
    const body = await c.req.parseBody({ all: true })
    const filesRaw = body['files']
    const filesArr = Array.isArray(filesRaw) ? filesRaw : (filesRaw ? [filesRaw] : [])
    const files = filesArr.filter((f): f is File => f instanceof File)

    if (files.length === 0) {
      throw new ctx.core.errors.ValidationError('No files provided')
    }

    const folder = typeof body['folder'] === 'string' ? body['folder'] as string : undefined
    const userId = c.get('user')?.id
    const storageService = getStorageService()
    const results = []

    for (const f of files) {
      if (f.name.includes('\x00')) {
        throw new ctx.core.errors.ValidationError('Invalid filename: contains null byte')
      }
      const buf = Buffer.from(await f.arrayBuffer())
      const file: UploadedFile = {
        fieldname: 'files',
        originalname: f.name,
        mimetype: f.type || 'application/octet-stream',
        buffer: buf,
        size: f.size
      }
      const result = await storageService.upload(file, { folder, userId })
      results.push(result)
    }

    return c.json(results, 201)
  }) as OpenApiHandler)

  // ============================================================================
  // GET /stats — dashboard
  // ============================================================================

  const statsRoute = createRoute({
    method: 'get',
    path: '/stats',
    tags: ['Storage'],
    summary: 'Aggregate storage stats',
    security: auth ? [{ bearerAuth: [] }] : [],
    middleware: (auth ? [auth] : []) as unknown as MiddlewareHandler<NexusEnv>[],
    responses: {
      200: { description: 'Stats', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(statsRoute, (async (c) => {
    const knex = ctx.db.knex
    const stats = await knex('storage_files')
      .select(
        knex.raw('COUNT(*) as total_files'),
        knex.raw('COALESCE(SUM(size), 0) as total_size'),
        knex.raw('COUNT(DISTINCT folder) as total_folders')
      )
      .first()

    const totalSize = Number((stats as { total_size?: number | string } | undefined)?.total_size ?? 0)
    return c.json({
      total_files: Number((stats as { total_files?: number | string } | undefined)?.total_files ?? 0),
      total_size: formatBytes(totalSize),
      total_folders: Number((stats as { total_folders?: number | string } | undefined)?.total_folders ?? 0)
    })
  }) as OpenApiHandler)

  const statsByTypeRoute = createRoute({
    method: 'get',
    path: '/stats/by-type',
    tags: ['Storage'],
    summary: 'Files grouped by mimetype',
    security: auth ? [{ bearerAuth: [] }] : [],
    middleware: (auth ? [auth] : []) as unknown as MiddlewareHandler<NexusEnv>[],
    responses: {
      200: { description: 'Items', content: { 'application/json': { schema: z.array(anyObject) } } }
    }
  })
  app.openapi(statsByTypeRoute, (async (c) => {
    const knex = ctx.db.knex
    const rows = await knex('storage_files')
      .select('mimetype')
      .count('* as count')
      .sum('size as total_size')
      .groupBy('mimetype')
      .orderBy('count', 'desc')
      .limit(20)

    const items = (rows as { mimetype: string; count: string | number }[]).map(r => ({
      x: r.mimetype || 'unknown',
      y: Number(r.count)
    }))
    return c.json(items)
  }) as OpenApiHandler)

  const statsByFolderRoute = createRoute({
    method: 'get',
    path: '/stats/by-folder',
    tags: ['Storage'],
    summary: 'Files grouped by folder',
    security: auth ? [{ bearerAuth: [] }] : [],
    middleware: (auth ? [auth] : []) as unknown as MiddlewareHandler<NexusEnv>[],
    responses: {
      200: { description: 'Items', content: { 'application/json': { schema: z.array(anyObject) } } }
    }
  })
  app.openapi(statsByFolderRoute, (async (c) => {
    const knex = ctx.db.knex
    const rows = await knex('storage_files')
      .select(knex.raw("COALESCE(folder, '(root)') as folder"))
      .count('* as count')
      .sum('size as total_size')
      .groupBy('folder')
      .orderBy('count', 'desc')
      .limit(20)

    const items = (rows as { folder: string; count: string | number }[]).map(r => ({
      x: r.folder,
      y: Number(r.count)
    }))
    return c.json(items)
  }) as OpenApiHandler)

  // ============================================================================
  // GET /files — override list with ?tags= filter + ?include=tags enrichment
  //
  // Custom route registers before auto-mount, so it wins for THIS exact path.
  // CRUD for /files/{id} (GET, PUT) still falls through to auto-mount.
  // ============================================================================

  const listFilesRoute = createRoute({
    method: 'get',
    path: '/files',
    tags: ['Storage'],
    summary: 'List files (supports ?tags= filter and ?include=tags)',
    security: auth ? [{ bearerAuth: [] }] : [],
    middleware: (auth ? [auth] : []) as unknown as MiddlewareHandler<NexusEnv>[],
    responses: {
      200: { description: 'Paginated files', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(listFilesRoute, (async (c) => {
    const filesService = ctx.services.get<{
      findAll: (opts: Record<string, unknown>) => Promise<{ items: Record<string, unknown>[]; total: number; page: number; limit: number; totalPages: number; hasNext: boolean }>
    }>('storage_files')

    const query = c.req.query()
    const tagsParam = query['tags']
    const includeParam = query['include']

    let filters: Record<string, unknown> = {}
    try {
      filters = query['filters'] ? JSON.parse(query['filters']) : {}
    } catch {
      filters = {}
    }

    // 1. Tag filtering: resolve slugs → matching entity IDs
    if (tagsParam) {
      const taxonomyService = ctx.services.getOptional<{
        getEntityIdsByTags: (entityType: string, slugs: string[], match: 'all' | 'any') => Promise<string[]>
      }>('tags')
      if (taxonomyService?.getEntityIdsByTags) {
        const slugs = tagsParam.split(',').map(s => s.trim()).filter(Boolean)
        const tagMatch = (query['tagMatch'] === 'any') ? 'any' : 'all'
        const matchingIds = await taxonomyService.getEntityIdsByTags('storage.files', slugs, tagMatch)

        if (matchingIds.length === 0) {
          return c.json({ items: [], total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false })
        }
        filters = { ...filters, id: { $in: matchingIds } }
      }
    }

    const page = Number(query['page'] ?? 1)
    const limit = Number(query['limit'] ?? 20)
    const sort = query['sort']

    const result = await filesService.findAll({ filters, page, limit, sort })

    // 2. Enrich items with tags if requested
    if (includeParam?.includes('tags') && Array.isArray(result.items) && result.items.length > 0) {
      const taxonomyService = ctx.services.getOptional<{
        getTagsForEntities: (entityType: string, ids: string[]) => Promise<Map<string, unknown[]>>
      }>('tags')
      if (taxonomyService?.getTagsForEntities) {
        const ids = result.items.map(item => (item as { id?: string }).id).filter((id): id is string => !!id)
        if (ids.length > 0) {
          try {
            const tagsMap = await taxonomyService.getTagsForEntities('storage.files', ids)
            for (const item of result.items as Record<string, unknown>[]) {
              const itemId = item['id'] as string | undefined
              item['tags'] = itemId ? (tagsMap.get(itemId) ?? []) : []
            }
          } catch { /* best-effort enrichment */ }
        }
      }
    }

    return c.json(result)
  }) as OpenApiHandler)

  // ============================================================================
  // DELETE /files/{id} — override auto-mount for per-instance CASL + event
  // ============================================================================

  const deleteFileRoute = createRoute({
    method: 'delete',
    path: '/files/{id}',
    tags: ['Storage'],
    summary: 'Delete a file (with CASL instance check + entity.deleted event)',
    security: optionalAuth ? [{ bearerAuth: [] }] : [],
    middleware: (optionalAuth ? [optionalAuth] : []) as unknown as MiddlewareHandler<NexusEnv>[],
    request: { params: idParams },
    responses: {
      204: { description: 'File deleted' },
      403: { description: 'Forbidden', content: { 'application/json': { schema: anyObject } } },
      404: { description: 'Not found', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(deleteFileRoute, (async (c) => {
    const id = c.req.param('id')!
    const storageService = getStorageService()
    const file = await storageService.getById(id)
    if (!file) throw new ctx.core.errors.NotFoundError('File')

    // CASL instance check
    const ability = c.get('ability')
    if (ability) {
      const { subject, ForbiddenError: CASLForbiddenError } = ctx.core.abilities
      try {
        CASLForbiddenError.from(ability).throwUnlessCan(
          'delete',
          subject('StorageFile', file as unknown as Record<string, unknown>)
        )
      } catch {
        throw new ctx.core.errors.ForbiddenError('Not allowed to delete Storage Files')
      }
    }

    await storageService.delete(id)

    ctx.events.notify('entity.deleted', {
      module: 'storage',
      entity: 'files',
      action: 'deleted',
      id,
      timestamp: new Date().toISOString()
    })

    return c.body(null, 204)
  }) as OpenApiHandler)

  // ============================================================================
  // Attachments sub-router
  // ============================================================================

  const attachmentsRouter = createAttachmentsRoutes(ctx)
  app.route('/', attachmentsRouter)

  return app
}
